import React from 'react';
import {
  Dialog as ScrollableDialog,
  ScrollableDialogContent,
  DialogHeader as ScrollableDialogHeader,
  DialogTitle as ScrollableDialogTitle,
  DialogFooter as ScrollableDialogFooter,
} from '@/components/ui/scrollable-dialog';
import { Button } from '@/components/ui/button';
import { Loader2, Package, Upload } from 'lucide-react';
import {
  ShopifyProductFormFields,
  ShopifyStatusSelect,
  shopifyProductFormSummary,
  useShopifyProductForm,
} from './shopifyProductForm';


interface CreateShopifyProductDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedAccountId: string | null;
  shopCurrency?: string;
  onSuccess: () => void;
}

export const CreateShopifyProductDialog: React.FC<CreateShopifyProductDialogProps> = ({
  open, onOpenChange, selectedAccountId, shopCurrency = 'USD', onSuccess,
}) => {
  const form = useShopifyProductForm({
    open,
    accountId: selectedAccountId,
    shopCurrency,
  });

  const onSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if ((await form.submit()).ok) {
      onSuccess();
      onOpenChange(false);
    }
  };

  return (
    <ScrollableDialog open={open} onOpenChange={v => { if (!v) form.reset(); onOpenChange(v); }}>
      <ScrollableDialogContent className="max-w-3xl max-h-[90vh] p-0 overflow-hidden flex flex-col">

        <ScrollableDialogHeader className="px-6 py-4 border-b bg-white shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-50 text-emerald-600">
                <Package className="h-5 w-5" />
              </div>
              <div>
                <ScrollableDialogTitle className="text-base font-semibold">Create Product</ScrollableDialogTitle>
                <p className="text-xs text-muted-foreground mt-0.5">Add a new product to your Shopify store</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <ShopifyStatusSelect value={form.status} onChange={form.setStatus} />
            </div>
          </div>
        </ScrollableDialogHeader>

        <div className="overflow-y-auto flex-1">
          <form onSubmit={onSubmit} className="p-6">
            <ShopifyProductFormFields form={form} />
          </form>
        </div>

        <ScrollableDialogFooter className="px-6 py-4 border-t bg-white shrink-0">
          <div className="flex items-center justify-between w-full">
            <p className="text-xs text-slate-400">{shopifyProductFormSummary(form)}</p>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => { form.reset(); onOpenChange(false); }} disabled={form.loading}>
                Cancel
              </Button>
              <Button onClick={onSubmit} disabled={form.loading || !form.canSubmit} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                {form.loading
                  ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{form.loadingMsg}</>
                  : <><Upload className="mr-2 h-4 w-4" />Create Product</>}
              </Button>
            </div>
          </div>
        </ScrollableDialogFooter>
      </ScrollableDialogContent>
    </ScrollableDialog>
  );
};

export default CreateShopifyProductDialog;
